package dev.lingvo.ops.pages.workspaces.orgId

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.ops.api.OpsApi
import dev.lingvo.ops.components.OpsCapNotice
import dev.lingvo.ops.components.OpsFact
import dev.lingvo.ops.components.OpsPageHeader
import dev.lingvo.ops.components.OpsPanel
import dev.lingvo.ops.components.OpsResourceStates
import dev.lingvo.ops.components.OpsShell
import dev.lingvo.ops.components.OpsWorkspaceNav
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page("/workspaces/{orgId}")
@Composable
fun WorkspacePage() {
    val id = rememberPageContext().route.params["orgId"].orEmpty()
    OpsShell("Workspaces") {
        val identity = rememberOpsResource(id, OpsApi::workspace)
        val members = rememberOpsResource(id, OpsApi::members)
        val projects = rememberOpsResource(id, OpsApi::projects)
        val invitations = rememberOpsResource(id, OpsApi::invitations)
        val github = rememberOpsResource(id, OpsApi::github)
        val deletion = rememberOpsResource(id, OpsApi::deletionScope)
        fun refresh() { identity.refresh(); members.refresh(); projects.refresh(); invitations.refresh(); github.refresh(); deletion.refresh() }
        val refreshing = identity.state.isRefreshing || members.state.isRefreshing || projects.state.isRefreshing ||
            invitations.state.isRefreshing || github.state.isRefreshing || deletion.state.isRefreshing
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            Link("/workspaces") { Text("Back to workspaces") }
            OpsPageHeader("Workspace", "Review ownership, access and configuration before investigating a specific record.", refreshing, ::refresh)
            OpsWorkspaceNav(id, "Overview")
            OpsResourceStates(identity.state, "Workspace not found", "This workspace has no accessible record.", retry = identity::refresh) { workspace ->
                OpsPanel("Workspace identity") {
                    OpsFact("Name", workspace.name)
                    OpsFact("Slug", workspace.slug)
                    OpsFact("Plan", workspace.plan.displayName)
                    OpsFact("Status", workspace.planStatus.name)
                    OpsFact("Owners", formatCount(workspace.ownerCount))
                    OpsFact("Ownership", if (workspace.ownerless) "Ownerless — needs review" else "Owner recorded")
                    OpsFact("Created", formatUtc(workspace.createdAt))
                }
                OpsPanel("Seats", "A mismatch may be in flight after a sync. Compare the counts with the last recorded sync before diagnosing a fault.") {
                    OpsFact("Granted", formatCount(workspace.seatsGranted))
                    OpsFact("Billed", workspace.seatsBilled?.let(::formatCount), absent = "Unknown")
                    OpsFact("Last sync", formatUtc(workspace.seatsSyncedAt, absent = "Not recorded"))
                    OpsFact("Sync grace window", "${workspace.seatSyncGraceSeconds} seconds")
                }
                OpsPanel("Billing links", "Presence is recorded linkage; it does not prove a payment was collected.") {
                    OpsFact("Subscription", if (workspace.hasSubscription) "Linked" else "None")
                    OpsFact("Subscription count", formatCount(workspace.subscriptionCount))
                    OpsFact("Customer", if (workspace.hasCustomer) "Linked" else "None")
                    Link("/workspaces/$id/billing") { Text("Inspect billing records") }
                }
            }
            OpsPanel("Members") {
                OpsResourceStates(members.state, "No members returned", "No member rows were returned for this workspace.", retry = members::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "members")
                    window.rows.forEach { row -> P { Link("/users/${row.userId}") { Text(row.name) }; Text(" · ${row.role.name} · ${row.lastActiveBucket}") } }
                }
            }
            OpsPanel("Projects") {
                OpsResourceStates(projects.state, "No projects", "No project rows were returned.", retry = projects::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "projects")
                    window.rows.forEach { row -> P { Text("${row.name} · ${row.slug} · ${formatCount(row.keys)} keys · ${formatCount(row.locales)} locales") } }
                }
            }
            OpsPanel("Invitations") {
                OpsResourceStates(invitations.state, "No invitations", "No invitation rows were returned.", retry = invitations::refresh) { window ->
                    OpsCapNotice(window.hitCap, window.cappedAt, "invitations")
                    window.rows.forEach { row -> P { Text("${row.email} · ${row.role.name} · expires ${formatUtc(row.expiresAt)}") } }
                }
            }
            OpsPanel("GitHub connection") {
                OpsResourceStates(github.state, "No connection result", "The connection status has not been returned.", retry = github::refresh) { connection ->
                    OpsFact("Installation", if (connection.installed) "Installed" else "Not installed")
                    OpsFact("Connected", formatUtc(connection.connectedAt, absent = "Not recorded"))
                }
            }
            OpsPanel("Deletion scope", "Recorded scope only. This console does not delete the workspace.") {
                OpsResourceStates(deletion.state, "No deletion scope", "The scope could not be determined.", retry = deletion::refresh) { scope ->
                    OpsFact("Projects", formatCount(scope.projects))
                    OpsFact("Keys", formatCount(scope.keys))
                    OpsFact("Translations", formatCount(scope.translations))
                    OpsFact("Members", formatCount(scope.members))
                    OpsFact("Tokens", formatCount(scope.tokens))
                    OpsFact("Webhooks", formatCount(scope.webhooks))
                }
            }
        }
    }
}
